import {AfterViewInit, Component, Input, ViewChild} from '@angular/core';
import {MouseEvent, MapsAPILoader, AgmMap} from '@agm/core';
import {AddressLookupService} from '../address-lookup.service';
import {GetAddressIoAddressToAddressTransformer} from '../../transformers/get-address-io-address-to-address.transformer';
import {Address} from '../../domain/address';
import {W3wService} from '../w3w.service';
import {QuestionBase} from '../../domain/question.base';

export interface Marker {
  lat: number;
  lng: number;
  label?: string;
  draggable: boolean;
}

@Component({
  selector: 'crds-question-google-maps-marker',
  template: `
    <!-- QUESTION HINT -->
    <crds-question-hint [question]="question" class="mb-2"></crds-question-hint>
    <div *ngIf="lat" class="outer-wrapper">
      <div class="map-wrapper" id="wrapper" #wrapper>
        <agm-map
          [latitude]="lat"
          [longitude]="lng"
          [zoom]="zoom"
          [mapTypeId]="mapStyle"
          [disableDefaultUI]="true"
          [streetViewControl]="false"
          [zoomControl]="true"
          (mapClick)="mapClicked($event)">
          <agm-marker
            *ngFor="let m of markers; let i = index"
            (markerClick)="clickedMarker(m.label, i)"
            [latitude]="m.lat"
            [longitude]="m.lng"
            [label]="m.label"
            [markerDraggable]="m.draggable"
            (dragEnd)="markerDragEnd(m, $event)"
            #AgmMap>
            <agm-info-window>
              <div>{{w3w ? 'what3words: ' + w3w : 'Your location'}}</div>
            </agm-info-window>
          </agm-marker>
        </agm-map>
      </div>
    </div>
  `,
  styles: [
      `
      .map-wrapper {
        height: 300px;
      }

      agm-map {
        height: 100%;
        width: 100%;
        margin: 0;
        padding: 0;
      }

      :host ::ng-deep agm-map > div {
        margin-top: 0.75rem;
      }

      .outer-wrapper {
        height: 100%;
        width: 100%;
        padding: 0;
        margin: 0;
      }
    `
  ]
})
export class GoogleMapsMarkerQuestionComponent implements AfterViewInit {
  @Input() form;
  @Input() question: QuestionBase<any>;

  @ViewChild('AgmMap') agmMap: AgmMap;

  w3w: string;

  // google maps zoom level
  zoom = 18;

  // initial center position for the map
  lat: number;
  lng: number;
  markers: Marker[] = [];

  constructor(private mapsAPILoader: MapsAPILoader,
              private addressLookupService: AddressLookupService,
              private arrayToAddressTransformer: GetAddressIoAddressToAddressTransformer,
              private w3wService: W3wService) {
  }

  get mapStyle(): 'roadmap' | 'hybrid' | 'satellite' | 'terrain' | string {
    return this.question.mapsConfig && this.question.mapsConfig.mapStyle || 'hybrid';
  }

  ngAfterViewInit() {
    if (this.form.get('address').value) {
      this.updateValues(
        parseFloat(this.form.get('address').value.latitude),
        parseFloat(this.form.get('address').value.longitude)
      );
    }
    this.addressLookupService.addresses
      .subscribe((addresses: any[]) => {
        const address: Address = this.arrayToAddressTransformer.transform(addresses[0]);
        this.updateValues(address.latitude, address.longitude);
      });
  }

  mapClicked($event: MouseEvent) {
    this.updateFromEvent($event);
  }

  clickedMarker(label: string, index: number) {
    console.log(`clicked the marker: ${label || index}`);
  }

  markerDragEnd(m: Marker, $event: MouseEvent) {
    this.updateFromEvent($event);
  }

  private setMarkers(lat: number, lng: number) {
    this.markers.length = 0;
    this.markers.push({
      lat: lat,
      lng: lng,
      draggable: true
    });
  }

  private updateFromEvent($event: MouseEvent) {
    this.updateValues($event.coords.lat, $event.coords.lng);
    this.lat = $event.coords.lat;
    this.lng = $event.coords.lng;
    this.updateWhat3Words();
  }

  private updateValues(lat: number, lng: number) {
    if (!!lat && !!lng) {
      setTimeout(() => {
        this.setMarkers(lat, lng);
        this.lat = lat;
        this.lng = lng;
        this.form.get(this.question.key).patchValue(`${this.lat},${this.lng}`);
        const address = this.form.get('address').value;
        address.latitude = this.lat;
        address.longitude = this.lng;
        this.form.get('address').patchValue(address);
      });
    }
  }

  private updateWhat3Words() {
      const what3Words = this.form.get('what3Words');
      const self = this;
      if (what3Words && self.lat && self.lng) {
        this.w3wService.lookupByLatLng(self.lat, self.lng)
          .subscribe(result => {
            what3Words.patchValue(result['words']);
            self.w3w = result['words'];
          });
      }
  }
}
